import api from './api';
import socketService from './socketService';

// Cache pour eviter les appels trop fréquents
let lastFetchTime = 0;
const CACHE_DURATION = 5000; // 5 secondes min entre les appels
let cachedUnreadNotifications = null;

/**
 * Service pour gérer les notifications
 */
const notificationService = {
    /**
     * Initialiser le service de notification
     * @param {Function} onNewNotification - Callback pour les nouvelles notifications
     * @param {Function} onNotificationRead - Callback pour les notifications lues
     * @param {Function} onNotificationDeleted - Callback pour les notifications supprimées
     * @param {Function} onAllNotificationsRead - Callback pour toutes les notifications lues
     * @returns {Promise<boolean>} Succès de l'initialisation
     */
    async initialize(onNewNotification, onNotificationRead, onNotificationDeleted, onAllNotificationsRead) {
        try {
            // Connecter au serveur Socket.io
            await socketService.connect();

            // Écouter les événements de notification
            socketService.on('notification', (notification) => {
                if (onNewNotification) {
                    onNewNotification(notification);
                }
            });

            socketService.on('notification_read', (data) => {
                if (onNotificationRead) {
                    onNotificationRead(data.id);
                }
            });

            socketService.on('notification_deleted', (data) => {
                if (onNotificationDeleted) {
                    onNotificationDeleted(data.id);
                }
            });

            socketService.on('all_notifications_read', () => {
                if (onAllNotificationsRead) {
                    onAllNotificationsRead();
                }
            });

            return true;
        } catch (error) {
            console.error('[notificationService] Socket init failed; notifications will rely on REST until reconnect.', {
                message: error?.message,
                description: error?.description,
                type: error?.type,
                context: error?.context,
                data: error?.data
            });
            return false;
        }
    },

    /**
     * Nettoyer les écouteurs d'événements
     */
    cleanup() {
        socketService.off('notification');
        socketService.off('notification_read');
        socketService.off('notification_deleted');
        socketService.off('all_notifications_read');
    },

    /**
     * Récupérer toutes les notifications avec pagination et filtrage
     * @param {Object} options - Options de pagination et de filtrage
     * @returns {Promise<Object>} Notifications et métadonnées
     */
    async getNotifications(options = {}) {
        try {
            const { page = 1, limit = 10, type, isRead, search } = options;

            let url = `/notifications?page=${page}&limit=${limit}`;

            if (type) {
                url += `&type=${type}`;
            }

            // Ne pas ajouter le paramètre isRead s'il est undefined ou vide
            if (isRead !== undefined && isRead !== '') {
                url += `&isRead=${isRead}`;
            }

            if (search) {
                url += `&search=${encodeURIComponent(search)}`;
            }

            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching notifications:', error);
            throw error;
        }
    },

    /**
     * Récupérer les notifications non lues
     * @returns {Promise<Array>} Notifications non lues
     */
    async getUnreadNotifications() {
        try {
            const now = Date.now();
            
            // Vérifier le cache
            if (cachedUnreadNotifications && (now - lastFetchTime) < CACHE_DURATION) {
                console.log('Notifications from cache');
                return cachedUnreadNotifications;
            }
            
            const response = await api.get('/notifications/unread');
            lastFetchTime = now;
            cachedUnreadNotifications = response.data;
            return response.data;
        } catch (error) {
            // Si erreur 429 (rate limit), retourner le cache ou vide
            if (error?.response?.status === 429) {
                console.warn('Rate limited on notifications, returning cached data');
                return cachedUnreadNotifications || { notifications: [], count: 0 };
            }
            // Pour autres erreurs, retourner aussi une réponse vide plutôt que de lever
            console.warn('Error fetching unread notifications:', error);
            return { notifications: [], count: 0 };
        }
    },

    /**
     * Marquer une notification comme lue
     * @param {string} notificationId - ID de la notification
     * @returns {Promise<Object>} Notification mise à jour
     */
    async markAsRead(notificationId) {
        try {
            const response = await api.patch(`/notifications/${notificationId}/read`);
            return response.data;
        } catch (error) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    },

    /**
     * Marquer toutes les notifications comme lues
     * @returns {Promise<Object>} Résultat de l'opération
     */
    async markAllAsRead() {
        try {
            const response = await api.patch('/notifications/read-all');
            return response.data;
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            throw error;
        }
    },

    /**
     * Supprimer une notification
     * @param {string} notificationId - ID de la notification
     * @returns {Promise<Object>} Résultat de l'opération
     */
    async deleteNotification(notificationId) {
        try {
            const response = await api.delete(`/notifications/${notificationId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting notification:', error);
            throw error;
        }
    },

    /**
     * Supprimer toutes les notifications
     * @returns {Promise<Object>} Résultat de l'opération
     */
    async deleteAllNotifications() {
        try {
            const response = await api.delete('/notifications');
            return response.data;
        } catch (error) {
            console.error('Error deleting all notifications:', error);
            throw error;
        }
    },

    /**
     * Envoyer une notification de test (pour le développement)
     * @param {Object} notification - Données de la notification
     * @returns {Promise<Object>} Notification créée
     */
    async sendTestNotification(notification) {
        try {
            const response = await api.post('/notifications/test', notification);
            return response.data;
        } catch (error) {
            console.error('Error sending test notification:', error);
            throw error;
        }
    }
};

export default notificationService;